import {
  ICalculatorBag,
  IRustmixBuyer,
  IRustmixBuyerOffer,
} from '../ts/rustmix-buyer.types';

export class CalculatorService {
  static weighted_average(
    value_a: number,
    value_b: number,
    weight_a: number,
    weight_b: number,
  ): number {
    return (+value_a * +weight_a + +value_b * +weight_b) / (+weight_a + +weight_b);
  }

  static hfn_average(
    value_a: number,
    value_b: number,
    weight_a: number,
    weight_b: number,
  ): number {
    if (value_a === 50.0 || value_b === 50.0) {
      return 0.0;
    }
    let da = (weight_a * 6000.0) / (value_a - 50.0);
    let db = (weight_b * 6000.0) / (value_b - 50.0);
    return 6000.0 / ((da + db) / (weight_a + weight_b)) + 50.0;
  }

  static price_bags(
    prices: IRustmixBuyerOffer[],
    bagA: ICalculatorBag,
    bagB: ICalculatorBag,
    enableMoistenUnmixed: boolean,
    targetMoisture: number,
    expertMode: boolean,
  ) {
    let determine_price = (bag: any): any | null => {
      let fallthrough_flag = false;
      for (let range of prices) {
        if (
          range.protein < bag.protein + 0.00001 &&
          !fallthrough_flag
        ) {
          if (check_fallthrough(bag, range, expertMode)) {
            return range;
          } else {
            fallthrough_flag = true;
            continue;
          }
        } else if (fallthrough_flag) {
          if (check_fallthrough(bag, range, expertMode)) {
            fallthrough_flag = false;
            return range;
          }
        }
      }
      return null;
    };

    let check_fallthrough = (bag: any, range: any, expertMode: boolean): boolean => {
      let moisture_mod: number = bag.humidity;
      if (expertMode && bag.humidity > targetMoisture) {
        moisture_mod = targetMoisture;
      }
      if (
        parseFloat(String(range.gluten)) <= parseFloat(bag.gluten) &&
        parseFloat(String(range.idk)) >= parseFloat(bag.idk) &&
        parseFloat(String(range.clop)) >= parseFloat(bag.bug) &&
        parseFloat(String(range.cp)) <= parseFloat(bag.cp) &&
        parseFloat(String(range.nature)) <= parseFloat(bag.nature) &&
        parseFloat(String(range.humidity)) >= moisture_mod
      ) {
        return true;
      } else return false;
    };

    let price_a = determine_price({
      weight: parseFloat(String(+bagA.weight)),
      gluten: parseFloat(String(+bagA.gluten)),
      idk: parseFloat(String(+bagA.idk)),
      bug: parseFloat(String(+bagA.bug)),
      protein: parseFloat(String(+bagA.protein)),
      cp: parseFloat(String(+bagA.cp)),
      nature: parseFloat(String(+bagA.nature)),
      humidity: parseFloat(String(+bagA.humidity)),
    });
    let price_b = determine_price({
      weight: parseFloat(String(+bagB.weight)),
      gluten: parseFloat(String(+bagB.gluten)),
      idk: parseFloat(String(+bagB.idk)),
      bug: parseFloat(String(+bagB.bug)),
      protein: parseFloat(String(+bagB.protein)),
      cp: parseFloat(String(+bagB.cp)),
      nature: parseFloat(String(+bagB.nature)),
      humidity: parseFloat(String(+bagB.humidity)),
    });
    let price_m = determine_price({
      weight: +bagA.weight + +bagB.weight,
      gluten:
        +CalculatorService.weighted_average(
          parseFloat(String(+bagA.gluten)),
          parseFloat(String(+bagB.gluten)),
          parseFloat(String(+bagA.weight)),
          parseFloat(String(+bagB.weight)),
        ).toFixed(2) || 0,
      idk:
        +CalculatorService.weighted_average(
          parseFloat(String(+bagA.idk)),
          parseFloat(String(+bagB.idk)),
          parseFloat(String(+bagA.weight)),
          parseFloat(String(+bagB.weight)),
        ).toFixed(2) || 0,
      bug:
        +CalculatorService.weighted_average(
          parseFloat(String(+bagA.bug)),
          parseFloat(String(+bagB.bug)),
          parseFloat(String(+bagA.weight)),
          parseFloat(String(+bagB.weight)),
        ).toFixed(2) || 0,
      protein:
        +CalculatorService.weighted_average(
          parseFloat(String(+bagA.protein)),
          parseFloat(String(+bagB.protein)),
          parseFloat(String(+bagA.weight)),
          parseFloat(String(+bagB.weight)),
        ).toFixed(2) || 0,
      cp:
        +CalculatorService.weighted_average(
          parseFloat(String(+bagA.cp)),
          parseFloat(String(+bagB.cp)),
          parseFloat(String(+bagA.weight)),
          parseFloat(String(+bagB.weight)),
        ).toFixed(2) || 0,
      nature:
        +CalculatorService.hfn_average(
          parseFloat(String(+bagA.nature)),
          parseFloat(String(+bagB.nature)),
          parseFloat(bagA.weight),
          parseFloat(bagB.weight),
        ).toFixed(2) || 0,
      humidity:
        +CalculatorService.weighted_average(
          parseFloat(String(+bagA.humidity)),
          parseFloat(String(+bagB.humidity)),
          parseFloat(bagA.weight),
          parseFloat(bagB.weight),
        ).toFixed(2) || 0,
    });

    let bag_a_price_id;
    let bag_a_price;

    let bag_b_price_id;
    let bag_b_price;

    let bag_mixed_price_id;
    let bag_mixed_price;

    let bag_price_delta;

    if (price_a !== null) {
      bag_a_price_id = price_a.id;
      bag_a_price = price_a.price * +bagA.weight;
    } else {
      bag_a_price_id = 0;
      bag_a_price = 0;
    }

    if (price_b !== null) {
      bag_b_price_id = price_b.id;
      bag_b_price = price_b.price * +bagB.weight;
    } else {
      bag_b_price_id = 0;
      bag_b_price = 0.0;
    }

    if (price_m !== null) {
      bag_mixed_price_id = price_m.id;
      bag_mixed_price = price_m.price * (+bagA.weight + +bagB.weight);
    } else {
      bag_mixed_price_id = 0;
      bag_mixed_price = 0.0;
    }

    bag_price_delta = bag_mixed_price - (bag_a_price + bag_b_price);

    return {
      price_m,
      bag_a_price,
      bag_b_price,
      bag_mixed_price,
      bag_price_delta,
      price_a,
      price_b,
    };
  }

  static moisture_calc({
    targetMoisture,
    setShrincageCoeff,
    setState,
    shrincageCoeff,
    bagA,
    bagB,
    bagMixed,
    bagAPrice,
    bagBPrice,
    bagMixedPrice,
    enableMoistenUnmixed,
    setBagAWeightGain,
    setBagBWeightGain,
    setBagMixedWeightGain,
    setBagAPriceGain,
    setBagBPriceGain,
    setBagMixedPriceGain,
    setBagAPrice,
    setBagBPrice,
    setBagAMoistureCoeff,
    setBagBMoistureCoeff,
    setBagMixedMoistureCoeff,
  }: {
    targetMoisture: number;
    setShrincageCoeff: (val: number) => void;
    setState: any;
    shrincageCoeff: number;
    bagA: ICalculatorBag;
    bagB: ICalculatorBag;
    bagMixed: ICalculatorBag;
    bagAPrice: number;
    bagBPrice: number;
    bagMixedPrice: number;
    enableMoistenUnmixed: boolean;
    setBagAWeightGain: (val: number) => void;
    setBagBWeightGain: (val: number) => void;
    setBagMixedWeightGain: (val: number) => void;
    setBagAPriceGain: (val: number) => void;
    setBagBPriceGain: (val: number) => void;
    setBagMixedPriceGain: (val: number) => void;
    setBagAPrice: (val: number) => void;
    setBagBPrice: (val: number) => void;
    setBagAMoistureCoeff: (val: number) => void;
    setBagBMoistureCoeff: (val: number) => void;
    setBagMixedMoistureCoeff: (val: number) => void;
  }) {
    setShrincageCoeff(100 / (100 - targetMoisture));
    const shrincageCoeffLocal = 100 / (100 - targetMoisture);
    let ma = (+bagA.humidity - targetMoisture) * shrincageCoeffLocal;
    let mb = (+bagB.humidity - targetMoisture) * shrincageCoeffLocal;
    let mx = (+bagMixed.humidity - targetMoisture) * shrincageCoeffLocal;
    const bagAMoistureCoeff = (100.0 - ma) / 100.0;
    setBagAMoistureCoeff(bagAMoistureCoeff * 100 - 100);
    const bagBMoistureCoeff = (100.0 - mb) / 100.0;
    setBagBMoistureCoeff(bagBMoistureCoeff * 100 - 100);
    const bagMixedMoistureCoeff = (100.0 - mx) / 100.0;
    setBagMixedMoistureCoeff(bagMixedMoistureCoeff * 100 - 100);

    const bagAWeightGain = bagAMoistureCoeff * +bagA.weight - +bagA.weight;
    setBagAWeightGain(bagAWeightGain);
    const bagBWeightGain = bagBMoistureCoeff * +bagB.weight - +bagB.weight;
    setBagBWeightGain(bagBWeightGain);
    const bagMixedGain = bagMixedMoistureCoeff * +bagMixed.weight - +bagMixed.weight;
    setBagMixedWeightGain(bagMixedGain);

    const bagAPriceGain = bagAPrice * bagAMoistureCoeff - bagAPrice;
    setBagAPriceGain(bagAPriceGain);
    const bagBPriceGain = bagBPrice * bagBMoistureCoeff - bagBPrice;
    setBagBPriceGain(bagBPriceGain);
    const bagMixedPriceGain = bagMixedPrice * bagMixedMoistureCoeff - bagMixedPrice;
    setBagMixedPriceGain(bagMixedPriceGain);

    let bag_coeff_adj = (bag: ICalculatorBag, bagMoistureCoeff: number): number => {
      if (bagMoistureCoeff > 1.0 && !enableMoistenUnmixed) return 1.0;
      else return bagMoistureCoeff;
    };
    let bag_a_price = bag_coeff_adj(bagA, bagAMoistureCoeff) * bagAPrice;
    setBagAPrice(bag_a_price);
    let bag_b_price = bag_coeff_adj(bagB, bagBMoistureCoeff) * bagBPrice;
    setBagBPrice(bag_b_price);
    const priceDelta = bagMixedPrice + bagMixedPriceGain - (bag_a_price + bag_b_price);
    const bagMixedProfit = (bagMixedPrice - bagMixedPriceGain) - ((bag_a_price - bagAPriceGain) + (bagBPrice - bagBPriceGain));
    // console.log(`${bagMixedPrice} + ${bagMixedPriceGain} - (${bag_a_price} + ${bagBPrice})`)
    // console.log(priceDelta)
    // console.log(bagMixedProfit)
    return {
      bagAMoistureCoeff,
      bagBMoistureCoeff,
      bagMixedMoistureCoeff,
      bagAWeightGain,
      bagBWeightGain,
      bagMixedGain,
      bagAPriceGain,
      bagBPriceGain,
      bagAPrice: bag_a_price,
      bagBPrice: bag_b_price,
      bagMixedProfit: priceDelta,
      bagMixedPrice,
    };
  }
}
